import { useCallback, useEffect, useRef, useState } from 'react';

interface TableSearchInputProps {
  // Called with the search value. Debounced while typing, immediate on clear.
  onSearch: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  searchOnEnter?: boolean;
}

// Server-side search input for the table header. Debounces keystrokes so we
// don't fire a request per character (or waits for Enter with searchOnEnter);
// clearing fires immediately.
function TableSearchInput({
  onSearch,
  placeholder = 'Search…',
  debounceMs = 600,
  searchOnEnter = false,
}: TableSearchInputProps) {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPending = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  // Clear any pending debounce on unmount.
  useEffect(() => cancelPending, [cancelPending]);

  const handleChange = (value: string) => {
    setSearchQuery(value);
    if (searchOnEnter) return;
    setIsSearching(true);
    cancelPending();
    debounceRef.current = setTimeout(() => {
      onSearch(value);
      setIsSearching(false);
    }, debounceMs);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (searchOnEnter && event.key === 'Enter') {
      onSearch(searchQuery);
    }
  };

  const handleClear = () => {
    cancelPending();
    setSearchQuery('');
    setIsSearching(false);
    onSearch('');
  };

  return (
    <div className="search-wrapper">
      <div className="icon-wrapper">
        <i
          className={isSearching ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-magnifying-glass'}
        ></i>
      </div>
      <input
        aria-label="Search table"
        type="text"
        value={searchQuery}
        onChange={(val) => handleChange(val.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="table-search-input"
      />
      {searchQuery && (
        <button className="table-search_btn" onClick={handleClear}>
          <i className="fa-solid fa-xmark"></i>
        </button>
      )}
    </div>
  );
}

export default TableSearchInput;
